/**
 * Determines if an address is a street-level address (has a street number and street name).
 * Street-level addresses are more specific than just city/suburb/region addresses.
 *
 * @param address - The address string to check
 * @returns True if the address appears to be street-level, false otherwise
 *
 * @example
 * isStreetLevelAddress("123 Main Street, Auckland") // true
 * isStreetLevelAddress("15 Queen St, Wellington") // true
 * isStreetLevelAddress("Auckland") // false
 * isStreetLevelAddress("Auckland, New Zealand") // false
 * isStreetLevelAddress("Ponsonby, Auckland") // false
 */
export const isStreetLevelAddress = (address: string | null): boolean => {
    if (!address || address.trim().length === 0) {
        return false;
    }

    // Remove common trailing location info for more accurate detection
    // e.g., "123 Main St, Auckland, New Zealand" -> "123 Main St"
    const addressParts = address.split(',');
    const primaryPart = addressParts[0]?.trim() || '';

    // Check if the address starts with a number (street number)
    const startsWithNumber = /^\d+/.test(primaryPart);

    if (!startsWithNumber) {
        return false;
    }

    // Common street type keywords that indicate a street-level address
    const streetKeywords = [
        'street',
        'st',
        'road',
        'rd',
        'avenue',
        'ave',
        'drive',
        'dr',
        'lane',
        'ln',
        'way',
        'place',
        'pl',
        'court',
        'ct',
        'terrace',
        'tce',
        'crescent',
        'cres',
        'boulevard',
        'blvd',
        'highway',
        'hwy',
        'parkway',
        'pkwy',
        'close',
        'cl',
        'grove',
        'gr',
        'parade',
        'pde',
        'esplanade',
        'esp',
    ];

    // Check if any street keyword is present (case-insensitive)
    const lowerAddress = primaryPart.toLowerCase();
    const hasStreetKeyword = streetKeywords.some((keyword) =>
        lowerAddress.includes(keyword)
    );

    // A street-level address should have both a number and a street keyword
    return startsWithNumber && hasStreetKeyword;
};
